import {
  makeStyles,
  makeResetStyles,
  mergeClasses,
  shorthands,
  typographyStyles,
  createFocusOutlineStyle,
} from '@fluentui/react-components';
import { tokens } from '@fluentui-copilot/tokens';
import type { PromptStarterSlots, PromptStarterState } from './PromptStarter.types';
import type { SlotClassNames } from '@fluentui/react-components';

export const promptStarterClassNames: SlotClassNames<PromptStarterSlots> = {
  root: 'fai-PromptStarter',
  category: 'fai-PromptStarter__category',
  icon: 'fai-PromptStarter__icon',
  prompt: 'fai-PromptStarter__prompt',
  badge: 'fai-PromptStarter__badge',
};

const useRootResetStyles = makeResetStyles({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gridTemplateRows: 'auto auto',
  gridTemplateAreas: `
    "icon category"
    ".    prompt"
  `,
  backgroundColor: tokens.colorNeutralBackground1,
  color: tokens.colorNeutralForeground1,
  fontFamily: tokens.fontFamilyBase,
  cursor: 'pointer',
  outlineStyle: 'none',
  textAlign: 'left',
  gap: '0px 8px',
  padding: '6px 8px 8px 8px',
  ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1),
  borderRadius: tokens.borderRadiusMedium,
  '&:hover': {
    backgroundColor: tokens.colorNeutralBackground1Hover,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1Hover),
  },
  '&:active': {
    backgroundColor: tokens.colorNeutralBackground1Pressed,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1Pressed),
  },
  ...createFocusOutlineStyle(),
});

const useStyles = makeStyles({
  disabled: {
    cursor: 'not-allowed',
    backgroundColor: tokens.colorNeutralBackgroundDisabled,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStrokeDisabled),
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStrokeDisabled),
    },
    '&:active': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStrokeDisabled),
    },
  },
  icon: { gridArea: 'icon', display: 'flex', alignSelf: 'center' },

  category: {
    ...typographyStyles.body1Strong,
    gridArea: 'category',
  },
  prompt: { ...typographyStyles.body1, gridArea: 'prompt' },
  disabledText: {
    color: tokens.colorNeutralForegroundDisabled,
  },
  disabledBadge: {
    opacity: '40%',
  },
});

const useNextStyles = makeStyles({ root: { borderRadius: tokens.borderRadiusXLarge } });

/**
 * @deprecated Use PromptStarterV2 instead.
 * Deprecated on 10/21/2025
 */
export const usePromptStarterStyles_unstable = (state: PromptStarterState): PromptStarterState => {
  'use no memo';

  const { designVersion } = state;

  const styles = useStyles();
  const nextStyles = useNextStyles();
  const rootResetStyles = useRootResetStyles();
  state.root.className = mergeClasses(
    promptStarterClassNames.root,
    rootResetStyles,
    designVersion === 'next' && nextStyles.root,
    state.root.disabled && styles.disabled,
    state.root.className,
  );
  state.icon.className = mergeClasses(
    promptStarterClassNames.icon,
    styles.icon,
    state.root.disabled && styles.disabledText,
    state.icon.className,
  );
  state.category.className = mergeClasses(
    promptStarterClassNames.category,
    styles.category,
    state.category.className,
    state.root.disabled && styles.disabledText,
  );
  if (state.prompt) {
    state.prompt.className = mergeClasses(
      promptStarterClassNames.prompt,
      styles.prompt,
      state.root.disabled && styles.disabledText,
      state.prompt.className,
    );
  }
  if (state.badge) {
    state.badge.className = mergeClasses(
      promptStarterClassNames.badge,
      state.root.disabled && styles.disabledBadge,
      state.badge.className,
    );
  }

  return state;
};
